import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { BsSliders } from 'react-icons/bs';
import { t } from '@bay-email/core';
import BayDatepicker from '@bay-ui/BayDatepicker';
import BaySearchInput from '@bay-ui/BaySearchInput';
import BaySelect from '@bay-ui/BaySelect';
import './SingleListingDrawer.scss';

interface SelectOption {
	label: string;
	value: string;
}

interface ListingFilterDrawerProps {
	loading?: boolean;
	filterCount?: number;
	searchValue: string;
	onSearch: (value: string) => void;
	onSearchValueChange: (value: string) => void;
	searchPlaceholder?: string;
	searchComponent?: React.ReactNode;
	defaultExpanded?: boolean;
	showFilterButton?: boolean;
	onFilterButtonClick?: (isExpanded: boolean) => void;
	priceMin: string;
	priceMax: string;
	onPriceMinChange: (value: string) => void;
	onPriceMaxChange: (value: string) => void;
	startDate: string;
	endDate: string;
	onStartDateChange: (value: string) => void;
	onEndDateChange: (value: string) => void;
	statusOptions: SelectOption[];
	selectedStatus: SelectOption[];
	onStatusChange: (value: SelectOption[]) => void;
	propertyTypeOptions: SelectOption[];
	selectedPropertyType: SelectOption[];
	onPropertyTypeChange: (value: SelectOption[]) => void;
	onClose: () => void;
	onClearAll: () => void;
	onApplyFilter: () => void;
}

export const ListingFilterDrawer: React.FC<ListingFilterDrawerProps> = ({
	loading = false,
	filterCount = 0,
	searchValue,
	onSearch,
	onSearchValueChange,
	searchPlaceholder,
	searchComponent,
	defaultExpanded = false,
	showFilterButton = true,
	onFilterButtonClick,
	priceMin,
	priceMax,
	onPriceMinChange,
	onPriceMaxChange,
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	statusOptions,
	selectedStatus,
	onStatusChange,
	propertyTypeOptions,
	selectedPropertyType,
	onPropertyTypeChange,
	onClose,
	onClearAll,
	onApplyFilter,
}) => {
	const currentYear = new Date().getFullYear();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const hasActiveFilters = filterCount > 0;

	useEffect(() => {
		setIsExpanded(defaultExpanded);
	}, [defaultExpanded]);

	return (
		<>
			<div className='d-flex align-items-center justify-content-between'>
				<div>
					{showFilterButton && (
						<Button
							className={`filter-btn d-flex align-items-center mb-2 ${filterCount || isExpanded ? 'text-white bg-800' : ''}`}
							disabled={loading}
							variant='outline-secondary'
							onClick={() => {
								const nextExpanded = !isExpanded;
								setIsExpanded(nextExpanded);
								onFilterButtonClick?.(nextExpanded);
							}}
						>
							<BsSliders className='me-2' size={16} />
							<span className='d-block d-md-inline'>
								{t('Filter')}
								{filterCount > 0 ? ` | ${filterCount <= 10 ? filterCount : '10+'}` : false}
							</span>
						</Button>
					)}
				</div>
				{searchComponent || (
					<BaySearchInput
						className='m-0 listings-search-input'
						disabled={loading}
						showClear={false}
						placeholder={searchPlaceholder || t('Search by Address or MLS # ...')}
						onSearch={onSearch}
						onChange={(value: string) => {
							if (value === undefined || value === '') {
								onSearch('');
							} else {
								onSearchValueChange(value);
							}
						}}
						value={searchValue}
						inputProps={{ className: 'ps-0' }}
					/>
				)}
			</div>

			{isExpanded && (
				<fieldset className='border-0 p-0 m-0' disabled={loading}>
				<Row>
				<Col xs={6} className='pb-3'>
					<Form.Label className='fw-bold'>{t('Price')}</Form.Label>
					<Row>
						<Col className='pe-0'>
							<Form.Group>
								<Form.Label className='fs-7 text-500'>{t('MIN')}</Form.Label>
								<Form.Control
									placeholder='$0'
									value={priceMin}
									onChange={event => onPriceMinChange(event.target.value)}
								/>
							</Form.Group>
						</Col>

						<Col>
							<Form.Group>
								<Form.Label className='fs-7 text-500'>{t('MAX')}</Form.Label>
								<Form.Control
									placeholder='$9999'
									value={priceMax}
									onChange={event => onPriceMaxChange(event.target.value)}
								/>
							</Form.Group>
						</Col>
					</Row>
				</Col>

				<Col xs={6} className='pb-3 ps-0'>
					<Form.Label className='fw-bold'>{t('Date Listed')}</Form.Label>

					<Row>
						<Col className='pe-0'>
							<Form.Group>
								<Form.Label className='fs-7 text-500'>{t('START')}</Form.Label>
								<BayDatepicker
									id='listing-filter-start-date'
									dateValue={startDate}
									fromYear={currentYear - 10}
									toYear={currentYear + 1}
									onChangeFunc={(_: Date, __: boolean, formattedValue: string) =>
										onStartDateChange(formattedValue)
									}
								/>
							</Form.Group>
						</Col>

						<Col>
							<Form.Group>
								<Form.Label className='fs-7 text-500'>{t('END')}</Form.Label>
								<BayDatepicker
									id='listing-filter-end-date'
									dateValue={endDate}
									fromYear={currentYear - 10}
									toYear={currentYear + 1}
									onChangeFunc={(_: Date, __: boolean, formattedValue: string) =>
										onEndDateChange(formattedValue)
									}
								/>
							</Form.Group>
						</Col>
					</Row>
				</Col>

				<Col xs={6} className='pb-4'>
					<Form.Group>
						<Form.Label className='fw-bold'>{t('Status')}</Form.Label>
						<BaySelect
							isClearable={true}
							isMulti={true}
							allowSelectAll={false}
							hideSelectedOne={false}
							optionsArray={statusOptions}
							placeholder={t('Select')}
							controlledValue={selectedStatus}
							onChangeFunc={(value: SelectOption[] | null) => onStatusChange(value || [])}
						/>
					</Form.Group>
				</Col>

				<Col xs={6} className='pb-4 ps-0'>
					<Form.Group>
						<Form.Label className='fw-bold'>{t('Property Type')}</Form.Label>
						<BaySelect
							isClearable={true}
							isMulti={true}
							allowSelectAll={false}
							hideSelectedOne={false}
							optionsArray={propertyTypeOptions}
							placeholder={t('Select')}
							controlledValue={selectedPropertyType}
							onChangeFunc={(value: SelectOption[] | null) => onPropertyTypeChange(value || [])}
						/>
					</Form.Group>
				</Col>

				<Col className='d-flex justify-content-between'>
					<Button
						className='px-0'
						variant='link'
						onClick={() => {
							setIsExpanded(false);
							onClose();
						}}
					>
						{t('Close')}
					</Button>
					<div className='d-flex gap-3'>
						<Button className='text-secondary px-0' variant='link' onClick={onClearAll}>
							{t('Clear All')}
						</Button>
						<Button
							variant='primary'
							disabled={!hasActiveFilters || loading}
							onClick={() => {
								setIsExpanded(false);
								onApplyFilter();
							}}
						>
							{t('Apply Filter')}
						</Button>
					</div>
				</Col>
				</Row>
				</fieldset>
			)}
		</>
	);
};

export default ListingFilterDrawer;